<i18n src="./resources/locales.json"></i18n>

<template>
  <div :class="{ 'bp-monitoring-card': true, expanded }" :style="cssStyle">
    <div v-if="isLoading" class="loading-section">
      <div v-once class="loading-label">{{ $t('loading') }}</div>
      <v-progress-linear color="var(--v-accent-base)" indeterminate rounded height="6" />
    </div>
    <v-hover v-else v-slot="{ hover }">
      <div :class="kpiStatusPulseClass">
        <EcsBusinessProcessPassiveMonitoringCard :business-process-id="businessProcessId" />
        <v-expand-transition>
          <EcsBusinessProcessActiveMonitoringCard
            v-show="hover || isPinned"
            :business-process-id="businessProcessId"
            class="transition-fast-in-fast-out v-card--reveal"
          />
        </v-expand-transition>
      </div>
    </v-hover>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EcsBusinessProcessPassiveMonitoringCard from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessPassiveMonitoringCard';
import EcsBusinessProcessActiveMonitoringCard from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessActiveMonitoringCard';
import EcsBusinessProcessHealthMixins from '@/mixins/ecs-business-process-health/EcsBusinessProcessHealthMixins';

export default {
  name: 'EcsBusinessProcessMonitoringCard',

  components: {
    EcsBusinessProcessPassiveMonitoringCard,
    EcsBusinessProcessActiveMonitoringCard
  },

  mixins: [EcsBusinessProcessHealthMixins],

  props: {
    businessProcessId: {
      type: String,
      required: true
    },
    width: {
      type: Number,
      default: 500,
      required: true
    },
    expanded: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      kpiStatusPulseClass: '',
      ignoreStatusChange: true
    };
  },

  computed: {
    ...mapGetters('SETTINGS', ['supervisorDashboardSettings']),

    isLoading() {
      return !this.criticalKpi;
    },

    cssStyle() {
      const backgroundColor = this.getHealthColor((this.criticalKpi || {}).status);
      return { backgroundColor, borderColor: backgroundColor || 'var(--v-surface-base)' };
    },

    dashboardSettings() {
      return this.supervisorDashboardSettings.adaptiveDashboards.cards.find(
        settings => settings.businessProcessId === this.businessProcessId
      );
    },

    isPinned() {
      return this.dashboardSettings ? this.dashboardSettings.isPinned : false;
    }
  },

  watch: {
    'criticalKpi.status'(currentStatus, oldStatus) {
      if (this.ignoreStatusChange && oldStatus === undefined) {
        this.ignoreStatusChange = false;
      } else {
        this.kpiStatusPulseClass = `${this.getHealthStatusName(this.criticalKpi.status)}-pulse`;
      }
      this.$emit(
        'critical-status-changed',
        this.businessProcessId,
        this.getHealthColor((this.criticalKpi || {}).status)
      );
    }
  },

  async created() {
    this.requestBusinessProcessHealthConfiguration(this.businessProcessId);
    await this.requestBusinessProcessHealthThresholds(this.businessProcessId);
  },

  mounted() {
    if (this.businessProcessId) {
      this.$emit(
        'critical-status-changed',
        this.businessProcessId,
        this.getHealthColor((this.criticalKpi || {}).status)
      );
    }
  },

  methods: {
    ...mapActions('BUSINESS_PROCESSES', [
      'requestBusinessProcessHealthConfiguration',
      'requestBusinessProcessHealthThresholds'
    ])
  }
};
</script>

<style scoped lang="scss">
.bp-monitoring-card {
  min-width: 500px;
  height: 290px;
  border-radius: 8px;
  position: relative;
  display: grid;
  grid-auto-rows: auto;
  border-style: solid;
  width: 100%;
  border-width: 2px;

  transition: all 0.2s ease;
}

.bp-monitoring-card.expanded {
  height: 529px;
}

.loading-section {
  display: grid;
  grid-template-rows: auto 28px;
  padding: 0 16px;

  .loading-label {
    text-align: center;
    opacity: 0.75;
    padding-top: 180px;
  }
}

.v-card--reveal {
  bottom: 0;
  position: absolute;
  width: 100%;
  height: 100%;
}

.good-pulse,
.warning-pulse,
.danger-pulse {
  border-radius: 8px;
  transition: all 0.2s;
}

.ignore-animation {
  animation: none !important;
}

.good-pulse {
  animation: good-pulse 1.6s 0s ease-in-out 2;
}

.warning-pulse {
  animation: warning-pulse 1.6s 0s ease-in-out 2;
}

.danger-pulse {
  animation: danger-pulse 1.6s 0s ease-in-out 2;
}

@keyframes good-pulse {
  0% {
    box-shadow: 0 0 8px 6px transparent;
  }
  30% {
    box-shadow: 0 0 40px 10px var(--v-success-lighten1);
  }
  100% {
    box-shadow: 0 0 8px 6px transparent;
  }
}

@keyframes warning-pulse {
  0% {
    box-shadow: 0 0 8px 6px transparent;
  }
  30% {
    box-shadow: 0 0 40px 10px var(--v-warning-base);
  }
  100% {
    box-shadow: 0 0 8px 6px transparent;
  }
}

@keyframes danger-pulse {
  0% {
    box-shadow: 0 0 8px 6px transparent;
  }
  30% {
    box-shadow: 0 0 40px 10px var(--v-error-base);
  }
  100% {
    box-shadow: 0 0 8px 6px transparent;
  }
}
</style>
